import * as z from 'zod';
import { dateOnlyOf, orgCalendarDate, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { workDaySettingsOf } from '@/lib/utils/work-days/work-days';
import type { WorkDayInfo } from '../types/employee.types';

/** Working-day picker order, same as the legacy step (week starts on Monday). */
export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

/** Legacy hour / minute pickers: 1-12 hours and 0-59 minutes. */
export const WORK_HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);
export const WORK_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) => index);

export interface WorkTime {
  hour: number;
  min: number;
}

/** Every working day starts out at 8 h 0 min (legacy `Weekdays` constant). */
const DEFAULT_WORK_TIME: WorkTime = { hour: 8, min: 0 };

const workTimeSchema = z.object({
  hour: z
    .number()
    .int()
    .min(WORK_HOUR_OPTIONS[0])
    .max(WORK_HOUR_OPTIONS[WORK_HOUR_OPTIONS.length - 1]),
  min: z
    .number()
    .int()
    .min(WORK_MINUTE_OPTIONS[0])
    .max(WORK_MINUTE_OPTIONS[WORK_MINUTE_OPTIONS.length - 1]),
});

/**
 * One work-day setting of an HR term. The first setting applies from the start of the term; each later
 * one from its `EffectiveFrom` until the next one, so changing an employee's work days during a term adds
 * a setting instead of rewriting the days already counted (leave, attendance, TOIL). A saved setting that
 * is already in force is locked for the same reason, and a new change on a saved term starts today at the
 * earliest; a term not saved yet (HR entering an old term) has neither limit.
 */
const workScheduleSchema = z.object({
  /** Saved and already in force (its first day is today or earlier): shown, not editable. */
  IsLocked: z.boolean(),
  /** First day of the setting ("YYYY-MM-DD"); '' for the first setting, which starts with the term. */
  EffectiveFrom: z.string(),
  /** Selected working day names. */
  WorkDays: z.array(z.string()).min(1, { message: 'AT_LEAST_ONE_WORKING_DAY_REQUIRED' }),
  /** Hours per weekday, kept for unselected days too so re-ticking a day restores its last value. */
  WorkHours: z.record(z.string(), workTimeSchema),
});

export type WorkScheduleValues = z.infer<typeof workScheduleSchema>;

export const workingInfoSchema = z.object({
  /** The term's work-day settings, oldest first. */
  WorkSchedules: z.array(workScheduleSchema).min(1),
});

export type WorkingInfoValues = z.infer<typeof workingInfoSchema>;

const buildDefaultWorkHours = (): Record<string, WorkTime> =>
  Object.fromEntries(WEEKDAYS.map((day) => [day, { ...DEFAULT_WORK_TIME }]));

/** A fresh setting with no day ticked (used by "Reset" so nothing shares object references). */
export const createEmptySchedule = (effectiveFrom = ''): WorkScheduleValues => ({
  IsLocked: false,
  EffectiveFrom: effectiveFrom,
  WorkDays: [],
  WorkHours: buildDefaultWorkHours(),
});

/** A change of work days starts from the setting before it, with its first day still to pick. */
export const createScheduleFrom = (previous: WorkScheduleValues): WorkScheduleValues => ({
  ...structuredClone(previous),
  IsLocked: false,
  EffectiveFrom: '',
});

/** The employee's today in the organization's zone ("YYYY-MM-DD"), the earliest day a new change can start. */
export const workDaysToday = (): string => orgCalendarDate(new Date().toISOString());

export const getWorkingInfoDefaults = (): WorkingInfoValues => ({
  WorkSchedules: [createEmptySchedule()],
});

/** Selected days in weekday order. */
const orderSelectedDays = (workDays: string[]): string[] =>
  WEEKDAYS.filter((day) => workDays.includes(day));

/**
 * Average working hours per selected day, rounded to 2 decimals like the legacy
 * `computeAverageWorkingHourPerDay`. Returns 0 when no day is selected.
 */
export const computeAvgDailyWorkHour = (
  workDays: string[] | undefined,
  workHours: Record<string, WorkTime> | undefined
): number => {
  const days = orderSelectedDays(workDays ?? []);
  if (days.length === 0) return 0;
  const totalHours = days.reduce((sum, day) => {
    const { hour, min } = workHours?.[day] ?? DEFAULT_WORK_TIME;
    return sum + (hour * 60 + min) / 60;
  }, 0);
  return Number((totalHours / days.length).toFixed(2));
};

/** The setting the term ends with: its `AvgDailyWorkHour` and what the term shows as its work days. */
export const latestScheduleOf = (schedules: WorkScheduleValues[] | undefined) =>
  schedules?.[schedules.length - 1];

/** Decimal hours (8.5) as the hour / minute pickers hold them ({ hour: 8, min: 30 }). */
const toWorkTime = (hours: number): WorkTime => {
  const minutes = Math.round(hours * 60);
  return { hour: Math.floor(minutes / 60), min: minutes % 60 };
};

/** The hour / minute pickers' value as decimal hours, rounded to 2 decimals. */
const toDecimalHours = ({ hour, min }: WorkTime): number => Number((hour + min / 60).toFixed(2));

/** A saved term's settings; each one already in force (the first from the term's start) is locked. */
export const workingInfoFromRecord = (
  record: { WorkDayHours?: WorkDayInfo[]; HrPeriodStartDate?: string },
  today: string = workDaysToday()
): WorkingInfoValues => {
  const settings = workDaySettingsOf(record.WorkDayHours);
  if (settings.length === 0) return getWorkingInfoDefaults();
  const termStart = dateOnlyOf(record.HrPeriodStartDate);
  return {
    WorkSchedules: settings.map(({ effectiveFrom, days }, index) => {
      const schedule = createEmptySchedule(index === 0 ? '' : effectiveFrom);
      const start = index === 0 ? termStart : effectiveFrom;
      schedule.IsLocked = Boolean(start) && start <= today;
      days.forEach((entry) => {
        schedule.WorkHours[entry.WorkDay] = toWorkTime(entry.WorkingHours);
      });
      schedule.WorkDays = orderSelectedDays(days.map((entry) => entry.WorkDay));
      return schedule;
    }),
  };
};

/**
 * Every setting's days as `HRWorkDay` rows: the first without `EffectiveFrom`, each later one with its
 * first day as a date-only value (00:00:00 UTC, `toDateOnlyValue`). `WorkDays` and `AvgDailyWorkHour`
 * describe the latest setting.
 */
export const workingInfoToPayload = (
  values: WorkingInfoValues
): { WorkDays: string[]; WorkDayHours: WorkDayInfo[]; AvgDailyWorkHour: number } => {
  const latest = latestScheduleOf(values.WorkSchedules);
  return {
    WorkDays: orderSelectedDays(latest?.WorkDays ?? []),
    WorkDayHours: values.WorkSchedules.flatMap((schedule, index) => {
      const effectiveFrom = index === 0 ? undefined : toDateOnlyValue(schedule.EffectiveFrom);
      return orderSelectedDays(schedule.WorkDays).map((day) => ({
        WorkDay: day,
        WorkingHours: toDecimalHours(schedule.WorkHours[day] ?? DEFAULT_WORK_TIME),
        ...(effectiveFrom ? { EffectiveFrom: effectiveFrom } : {}),
      }));
    }),
    AvgDailyWorkHour: computeAvgDailyWorkHour(latest?.WorkDays, latest?.WorkHours),
  };
};

/**
 * Each change of work days needs its first day, after the term start and the change before it, and within
 * the term; on a saved term (`ItemId`) not before `today` either. Locked settings were checked when saved.
 * `path` is where the term sits in the form (e.g. `['HrTerms', 1]`).
 */
export const workSchedulesRefine = (
  values: WorkingInfoValues & {
    ItemId?: string;
    HrPeriodStartDate: string;
    HrPeriodEndDate: string;
  },
  ctx: z.RefinementCtx,
  path: (string | number)[],
  today: string = workDaysToday()
): void => {
  values.WorkSchedules.forEach((schedule, index) => {
    if (index === 0 || schedule.IsLocked) return;
    const issuePath = [...path, 'WorkSchedules', index, 'EffectiveFrom'];
    const day = schedule.EffectiveFrom;
    const previous =
      index === 1 ? values.HrPeriodStartDate : values.WorkSchedules[index - 1].EffectiveFrom;
    let message: string | undefined;
    if (!day) message = 'ERR_MSG_REQUIRED';
    else if (previous && day <= previous) message = 'WORK_DAYS_CHANGE_MUST_BE_AFTER_PREVIOUS';
    else if (values.HrPeriodEndDate && day > values.HrPeriodEndDate)
      message = 'WORK_DAYS_CHANGE_MUST_BE_WITHIN_HR_PERIOD';
    else if (values.ItemId && day < today) message = 'WORK_DAYS_CHANGE_CANNOT_BE_IN_PAST';
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, path: issuePath, message });
  });
};
